import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Save, RotateCcw, ShieldAlert } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  useGetConfigurationSettings,
  useUpdateConfigurationSettings,
} from "@/resources/configuration-settings/configuration-settings.api";

const DEFAULT_MIN_SEARCH_VOLUME = 100;
const DEFAULT_MIN_RANKED_COMPETITORS = 1;

function isValidNumericInput(value: string): boolean {
  if (value === "") return false;
  const n = Number(value);
  return Number.isFinite(n) && Number.isInteger(n) && n >= 0;
}

export function LandscapeConfigurationTab() {
  const { data: configData, isLoading } = useGetConfigurationSettings();
  const updateMutation = useUpdateConfigurationSettings();

  const [minSearchVolume, setMinSearchVolume] = useState(String(DEFAULT_MIN_SEARCH_VOLUME));
  const [minRankedCompetitors, setMinRankedCompetitors] = useState(String(DEFAULT_MIN_RANKED_COMPETITORS));
  const [hasChanges, setHasChanges] = useState(false);
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (!configData || initialized) return;
    const map: Record<string, string> = {};
    for (const s of configData) {
      map[s.alias] = s.value;
    }
    const sv = map["landscape_min_search_volume"];
    const rc = map["landscape_min_ranked_competitors"];
    setMinSearchVolume(
      sv !== undefined && !Number.isNaN(Number(sv)) ? String(Number(sv)) : String(DEFAULT_MIN_SEARCH_VOLUME)
    );
    setMinRankedCompetitors(
      rc !== undefined && !Number.isNaN(Number(rc)) ? String(Number(rc)) : String(DEFAULT_MIN_RANKED_COMPETITORS)
    );
    setInitialized(true);
  }, [configData, initialized]);

  const isValid = isValidNumericInput(minSearchVolume) && isValidNumericInput(minRankedCompetitors);

  const handleSave = () => {
    updateMutation.mutate(
      {
        settings: [
          { alias: "landscape_min_search_volume", value: String(Number(minSearchVolume)) },
          { alias: "landscape_min_ranked_competitors", value: String(Number(minRankedCompetitors)) },
        ],
      },
      {
        onSuccess: () => {
          toast.success("Configuration Saved", { description: "Landscape generation settings have been updated." });
          setHasChanges(false);
        },
      },
    );
  };

  const handleReset = () => {
    setMinSearchVolume(String(DEFAULT_MIN_SEARCH_VOLUME));
    setMinRankedCompetitors(String(DEFAULT_MIN_RANKED_COMPETITORS));
    setHasChanges(true);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-sm text-muted-foreground">Loading configuration...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {hasChanges && (
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-yellow-50 border border-yellow-300 text-yellow-700 text-xs font-medium">
          <ShieldAlert className="h-3.5 w-3.5 shrink-0" />
          You have unsaved changes
        </div>
      )}

      <div className="flex items-center justify-end gap-2">
        <Button
          variant="outline"
          size="sm"
          className="h-8 text-xs gap-1.5 rounded-lg bg-card"
          onClick={handleReset}
        >
          <RotateCcw className="h-3.5 w-3.5" />
          Reset to Default
        </Button>
        <Button
          size="sm"
          className={cn(
            "h-8 text-xs gap-1.5",
            (!hasChanges || !isValid) && "bg-muted text-muted-foreground hover:bg-muted",
          )}
          onClick={handleSave}
          disabled={!hasChanges || !isValid || updateMutation.isPending}
        >
          <Save className="h-3.5 w-3.5" />
          Save Configuration
        </Button>
      </div>

      <Card>
        <CardContent className="pt-6 space-y-6">
          <div className="space-y-1.5">
            <Label htmlFor="landscape-min-search-volume" className="text-sm font-medium">
              Minimum Search Volume
            </Label>
            <p className="text-xs text-muted-foreground">
              Keywords with monthly search volume below this threshold are excluded when loading landscape keywords.
            </p>
            <Input
              id="landscape-min-search-volume"
              type="number"
              min={0}
              step={1}
              value={minSearchVolume}
              onChange={(e) => {
                setMinSearchVolume(e.target.value);
                setHasChanges(true);
              }}
              className="w-32 h-9 rounded-lg bg-card tabular-nums"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="landscape-min-ranked-competitors" className="text-sm font-medium">
              Minimum Ranked Competitors
            </Label>
            <p className="text-xs text-muted-foreground">
              Keywords where fewer than this many competitor ASINs rank are excluded from landscape results.
            </p>
            <Input
              id="landscape-min-ranked-competitors"
              type="number"
              min={0}
              step={1}
              value={minRankedCompetitors}
              onChange={(e) => {
                setMinRankedCompetitors(e.target.value);
                setHasChanges(true);
              }}
              className="w-32 h-9 rounded-lg bg-card tabular-nums"
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
